<template>
  <div class="flex w-full justify-between gap-x-4 p-4">
    <EpDropdown
      v-model="categoryModel"
      :option-list="categoryList || []"
      max-show-count="5"
      class="dropdown"
    />
    <SearchBar
      placeholder="Search by project name"
      v-model="inputModel"
      class="search-bar"
    />
  </div>
  <Divider />
  <LoadingWithSlot
    :height="600"
    :is-loading="isLoading"
  >
    <p class="p-4 text-xs font-bold">
      Search Result <span class="text-primary">{{ formattedTotalCount }}</span>
    </p>
    <p
      v-if="!isLoading && !totalCount"
      class="flex h-40 items-center justify-center text-xs"
    >
      No Search Result Found
    </p>
    <DefiTable
      v-else
      :defi-list="defiList"
      v-model:sort-direction-model="sortDirectionModel"
      v-model:sort-field-model="sortFieldModel"
    />
  </LoadingWithSlot>
  <div
    v-if="hasNextPage"
    class="flex w-full items-center justify-center gap-x-2 p-4"
  >
    <BcIcon
      class="animate-spin"
      icon="loading"
      fill="white"
    />
    <span class="text-xs font-bold">Loading...</span>
  </div>
  <p class="p-4 text-xs font-bold text-default/40">
    {{ source }}
  </p>
</template>

<script setup lang="ts">
import { useInfiniteScroll } from '@vueuse/core'
import { BcIcon, BcTooltip } from 'xangle-ui'

import { useGetDefiCategoryList, useGetDefiDashboardList } from '~/api/service/ecosystem.service'
import DefiTable from '~/components/@pages/defi-dashboard/DefiTable.vue'
import EpIconButton from '~/components/button/EpIconButton.vue'
import EpDropdown from '~/components/dropdown/EpDropdown.vue'
import EpIconLink from '~/components/link/EpIconLink.vue'
import SearchBar from '~/components/search/SearchBar.vue'
import CommonTable from '~/components/table/CommonTable.vue'
import PercentTag from '~/components/tag/PercentTag.vue'
import InfoTooltip from '~/components/tooltip/InfoTooltip.vue'
import { TVL_TABLE_COLUMN } from '~/constants/defi.constant'
import { DEFI_CATEGORY_PARENT_TOOLTIP, DEFI_CATEGORY_TOOLTIP } from '~/constants/tooltip.constants'
import { routes } from '~/routes'
import { type defiCategoryType, type IDefiRequestParam, sortDirectionType } from '~/types/defi.type'
import { DefiModel } from '~/types/model/defi.model'

const inputModel = ref<string>('')
const categoryModel = ref<defiCategoryType | ''>('')
const sortFieldModel = ref('tvl')
const sortDirectionModel = ref<sortDirectionType>(sortDirectionType.DESC)

const { data: categoryList } = useGetDefiCategoryList()

const reqParam = computed((): IDefiRequestParam => {
  return {
    category: categoryModel.value,
    search: inputModel.value,
    field: DefiModel.convertSortField(sortFieldModel.value),
    direction: sortDirectionModel.value,
    page: 1,
    size: 1000,
  }
})

const defiList = ref<Array<DefiModel>>([])

const { data, isLoading, hasNextPage, fetchNextPage } = useGetDefiDashboardList(reqParam)

const totalCount = computed(() => data.value?.pages[0].totalCount)
const formattedTotalCount = computed(() => data.value?.pages[0].formattedTotalCount)
const source = computed(() => data.value?.pages[0].source)

watch(() => data.value, () => {
  if (data.value) {
    defiList.value = data.value.pages.reduce((acc, cur) => {
      return acc.concat(cur.defiList)
    }, Array<DefiModel>())
  }
}, { immediate: true })

onMounted(() => {
  useInfiniteScroll(window, () => {
    if (hasNextPage?.value) {
      fetchNextPage()
    }
  }, {
    distance: 100,
  })
})
</script>

<style scoped lang="scss">
.search-bar {
  @apply bg-default/5 border border-default/10 relative focus-within:border-primary hover:bg-default/10 w-1/2;
  transition-property: border, background-color;
  transition-duration: 0.3s;
  transition-timing-function: ease;

  @include mobile {
    @apply w-full;
  }
}
</style>